import type { Run } from "@/api/runs";
import { formatDuration } from "@/components/runs/run-duration";

/**
 * The run header's meta cluster, derived from the run detail payload ALONE.
 *
 * The mock's cluster is three figures — `3m 27s`, a timestamp, `$1.84`. Only
 * two of the three exist on the wire. `Run` (`api/runs.ts`, extending
 * `ProjectRun` in `api/projects.ts`) carries `run_id`, `flow_name`,
 * `tile_name`, `status`, `submitted_at`, `started_at`, `finished_at`,
 * `submitted_by`, `project_slug` and `invocations[]` — and no cost, price or
 * currency field of any kind. So the header renders no cost figure: this is
 * chrome, and a header is not the place to add a fetch or to print a number
 * the API never sent.
 *
 * That omission is also the safe side of flowstack#1089 finding 1, which is
 * still live in `CostPreviewPanel.tsx:100-108`: there the "estimate" BADGE is
 * gated on the server's `pricing_basis` but the FIGURE is not, so an older
 * server (absent field) yields a bare dollar amount with nothing qualifying
 * it. A figure and its qualification have to appear and disappear together;
 * with no cost field at all here, the only shape that satisfies that is to
 * render neither.
 */

/**
 * Wall-clock elapsed between `started_at` and `finished_at`, or `null`.
 *
 * WHY THE GUARD IS BOTH-TIMESTAMPS AND NOT A STATUS TEST. "Duration" has no
 * single definition across the lifecycle — a `running` run has no end, a
 * `failed_validation` run never started, and a `cancelled` run's end is when
 * the operator intervened rather than when the science finished. Rather than
 * pick one meaning and mislabel the other statuses with it, this returns a
 * figure only for the case where the arithmetic means exactly one thing: the
 * run both STARTED and REACHED a terminal state, so `finished - started` is
 * the wall time it occupied, whatever the outcome was. Every other case is
 * `null` and the header omits the field.
 *
 * Consequently there is no live-ticking value for an in-flight run. A clock
 * that advances is behaviour, not chrome, and would need a timer this
 * component does not have.
 *
 * Unparseable or inverted timestamps are `null` too — a header that printed
 * `NaN` or a negative span would be asserting something about the run that
 * the data does not support.
 *
 * The FORMATTING is `run-duration.ts`, the same function the workspace's
 * per-Tile duration column renders through, so the run's wall time reads
 * identically in the header and in the table below it.
 */
export function elapsedLabel(run: Pick<Run, "started_at" | "finished_at">): string | null {
  if (!run.started_at || !run.finished_at) return null;
  const started = Date.parse(run.started_at);
  const finished = Date.parse(run.finished_at);
  if (Number.isNaN(started) || Number.isNaN(finished)) return null;
  const seconds = (finished - started) / 1000;
  if (seconds < 0) return null;
  return formatDuration(seconds);
}

/**
 * The tile-count chip's label, pluralised.
 *
 * `invocations` is the per-Tile rollup the Flow runner emits at compile time —
 * one row per fanned child, exactly one for a single-Tile run — so its length
 * is a real API-provided count and not an inference from the flow name.
 */
export function tileCountLabel(run: Pick<Run, "invocations">): string {
  const count = run.invocations.length;
  return `${count} ${count === 1 ? "tile" : "tiles"}`;
}
